/**
 * Keep the screen on while the stage is up.
 *
 * A phone playing this is a phone nobody is touching for stretches of a piece,
 * and its own idle timer does not know the difference between "put down" and
 * "held, watched, waiting for the next entry". The screen goes dark mid-show and
 * the audience member has to wake it, find their place and start again — and
 * the seat they were holding was released the moment the socket dropped.
 *
 * The lock is released by the browser every time the page is hidden, and it is
 * NOT reacquired on its own. That is the whole reason this is a module and not
 * two lines at the end of boot(): one call works until the first notification,
 * app switch or lock-screen glance, and then quietly stops working for the rest
 * of the show. Re-requesting on visibilitychange is the working half.
 *
 * Everything here fails silent. iOS Safari had no Wake Lock before 16.4 and
 * desktop Firefox still has none, a request can be refused outright (low
 * battery, and Android does refuse), and none of that is worth a word on a
 * screen somebody is trying to play.
 */

// The spec's types are not in this project's lib, and pulling in a DOM lib
// upgrade for two method names is not the trade. Narrow to what is used.
interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", cb: () => void): void;
}
interface WakeLockLike {
  request(type: "screen"): Promise<WakeLockSentinelLike>;
}

export function keepScreenAwake(): void {
  const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  if (!wakeLock) return;

  let sentinel: WakeLockSentinelLike | null = null;
  let acquiring = false;

  async function acquire(): Promise<void> {
    // Requesting while hidden throws by spec, and the visibility handler below
    // is what covers coming back — so this is a return, not a retry.
    if (document.hidden || acquiring || (sentinel && !sentinel.released)) return;
    acquiring = true;
    try {
      sentinel = await wakeLock!.request("screen");
      // Released for reasons that are not a page hide as well: the OS taking
      // the screen, a battery-saver threshold. Drop the handle so the next
      // visibility change asks for a fresh one rather than trusting a dead one.
      sentinel.addEventListener("release", () => { sentinel = null; });
    } catch {
      // Refused. The phone dims on its own schedule, exactly as before this
      // existed, and saying so on the stage would help nobody.
      sentinel = null;
    } finally {
      acquiring = false;
    }
  }

  document.addEventListener("visibilitychange", () => { if (!document.hidden) void acquire(); });
  void acquire();
}
